import type {
  BlocAllowedIn,
  BlocDefinition,
  BlocProperty,
  BlocPropertyGroup,
  CasesValue,
  ParamRef,
  SerializableValue,
  SlotDefinition,
} from './bloc-definition';
import type { BlocCategory } from './bloc-categories';
import type {
  Cb2PropertyTypeFor,
  DoodBlockField,
  DoodFieldType,
  IBlockDescriptor,
  LegacyBlockField,
} from './descriptor';
import {
  type BlockCondition,
  type ConditionalPropertyValue,
  type DescriptorControlField,
  type PropertyValue,
  type PropertyValueDashboardVisibilityPredicate,
  type PropertyValueDescriptor,
  DESCRIPTOR_CONTROL_FIELD_BRAND,
  PROPERTY_VALUE_BRAND,
  paletteColorValueToParamRef,
  paramRef,
} from './property-value';
import {
  backfillLegacyFieldDefault,
  legacyAllowedRoles,
} from './legacy-field-projection';
import {
  lastSegment,
  toSnakeHandle,
} from './paths';
import type {
  DotPath,
  PathToNested,
  Split,
  UnionToIntersection,
} from './paths';
import type { TranslationField } from './translation-field';
import { expandSpacingBoxToGroupedFields } from './spacing-box';
import { createBlockConfigResolver } from './block-config-resolver';
import { markComposeAliasCanonical } from './compose-alias';
import type { ResolveBlockConfigInput, ResolveBlockConfigRuntimeInput } from './block-config-resolver';
import type {
  DoodBlockScenarioMatrix,
} from './scenario-matrix';
import {
  definitionSetupScenarios,
  EMPTY_SCENARIO_FACTORY,
  type DefinitionSetupScenariosFn,
} from './definition-setup-scenarios';

/* -------------------------------------------------------------------------- */
/* Shared field declarations                                                  */
/* -------------------------------------------------------------------------- */

export type SharedFieldDefinition = {
  name: string;
  type: DoodFieldType;
};

export type SharedFieldsDefinition = {
  props?: readonly SharedFieldDefinition[];
  descriptorControlFields?: readonly SharedFieldDefinition[];
};

/**
 * Nested accessor object built from a list of `{ name, type }` declarations.
 * A declaration like `{ name: 'colors.backgroundColor', type: 'color' }` yields
 * `{ colors: { backgroundColor: 'color' } }`, so `shared.props.colors.backgroundColor`
 * is the literal `'color'` usable as `propertyValue({ type: ..., value })`.
 *
 * Each item is processed independently via a mapped tuple so that the field
 * `type` resolves to a single literal instead of the union of all field types.
 */
type MapAccessors<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? PathToNested<List[Index]['name'], List[Index]['type']>
    : never;
};

type MapRequiredProps<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? PathToNested<List[Index]['name'], PropertyValue<List[Index]['type']>>
    : never;
};

type MapRequiredControls<List extends readonly SharedFieldDefinition[]> = {
  [Index in keyof List]: List[Index] extends SharedFieldDefinition
    ? PathToNested<List[Index]['name'], DescriptorControlField<List[Index]['type']>>
    : never;
};

export type SharedFieldAccessorsFor<
  List extends readonly SharedFieldDefinition[] | undefined,
> = List extends readonly SharedFieldDefinition[]
  ? UnionToIntersection<MapAccessors<List>[number]>
  : {};

export type SharedFieldAccessors<SharedFields extends SharedFieldsDefinition> = {
  props: SharedFieldAccessorsFor<SharedFields['props']>;
  descriptorControlFields: SharedFieldAccessorsFor<
    SharedFields['descriptorControlFields']
  >;
};

/**
 * Required return shape for the config factory: every declared prop path must
 * be present as a `PropertyValue` of the matching type, and every descriptor
 * control field as a `DescriptorControlField` of the matching type.
 */
export type RequiredPropValues<SharedFields extends SharedFieldsDefinition> =
  SharedFields['props'] extends readonly SharedFieldDefinition[]
    ? UnionToIntersection<MapRequiredProps<SharedFields['props']>[number]>
    : {};

export type RequiredDescriptorControlValues<
  SharedFields extends SharedFieldsDefinition,
> = SharedFields['descriptorControlFields'] extends readonly SharedFieldDefinition[]
  ? UnionToIntersection<MapRequiredControls<SharedFields['descriptorControlFields']>[number]>
  : {};

/* -------------------------------------------------------------------------- */
/* Block metadata                                                             */
/* -------------------------------------------------------------------------- */

export type DoodBlockMetadata<
  SharedFields extends SharedFieldsDefinition,
  Name extends string = string,
> =
  Omit<IBlockDescriptor, 'fields' | 'name'> & {
    name: Name;
    /** Exact Angular component class name, e.g. `AnchorBlockComponent`. */
    componentType: string;
    category: BlocCategory;
    description?: string;
    keywords: readonly string[];
    sharedFields: SharedFields;
    /** CB2 slots; projected to `block_collection` fields in CB1. */
    slots?: readonly SlotDefinition[];
    isRootBlock?: true;
    isModalRoot?: true;
    /** Hide from the Studio slot picker while keeping the definition registered. */
    hideFromPicker?: true;
    allowedIn?: readonly BlocAllowedIn[];
    /**
     * Values written only when a new bloc is created.
     * Distinct from `propertyValue.value`, which stays the fallback for saved data
     * that does not contain the key.
     */
    creationDefaults?: Readonly<Record<string, SerializableValue>>;
  };

/* -------------------------------------------------------------------------- */
/* Definition result                                                          */
/* -------------------------------------------------------------------------- */

export type DoodBlockDefinition<Config = Record<string, unknown>> = {
  blockDescriptor: IBlockDescriptor;
  /** CB2 projection — fully serializable, no brands, no functions. */
  blocDefinition: BlocDefinition;
  /** Raw config tree with `propertyValue` / `descriptorControlField` leaves. */
  properties: Config;
  /** Static config snapshot (conditions resolve to undefined without a context). */
  config: ResolvedConfig<Config>;
  configToOverride: OverrideConfig<Config>;
  /** Pure runtime merge + conditional resolution (framework-agnostic). */
  resolveConfig: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
  dashboardLayout: DashboardLayout<Config>;
  scenarios: DoodBlockScenarioFactory;
};

export type DoodBlocPropValue =
  | SerializableValue
  | null
  | { readonly [key: string]: DoodBlocPropValue }
  | readonly DoodBlocPropValue[];

export type DoodBlocProps<Config = Record<string, unknown>> = {
  [Key in keyof Config]?: DoodBlocPropValue;
};

export type DoodBlocSlots = Record<string, Record<string, string> | readonly string[]>;

export type DoodBlocData = {
  common?: {
    instanceName?: string;
    conditionalVisibility?: DoodBlocPropValue;
  };
  [key: string]: DoodBlocPropValue | undefined;
};

export type DoodBlocInstance<
  Type extends string,
  Config = Record<string, unknown>,
> = {
  type: Type;
  props?: DoodBlocProps<Config>;
  slots?: DoodBlocSlots;
  data?: DoodBlocData;
};

export type DashboardLayout<Config = unknown> = {
  /**
   * Dotted prop/controlField paths excluded from the CB2 dashboard layout
   * and from generated CB1 descriptor fields. The property stays in config
   * so existing block data still loads.
   */
  omitFields?: readonly string[];
  /**
   * Explicit tabs. When absent, a default single-tab layout is generated from
   * all declared props (minus `omitFields`) — see `buildDefaultDashboardLayout`.
   */
  tabs?: readonly DashboardTab<Config>[];
};

/** Factory input: auto-generate a single tab with all config leaves. */
export type DashboardLayoutDisplayAll = 'displayAll';

export const displayAllDashboardLayout: DashboardLayoutDisplayAll = 'displayAll';

export type DashboardLayoutFactoryInput<Config = unknown> =
  | DashboardLayoutDisplayAll
  | DashboardLayoutOmitOnly<Config>
  | DashboardLayoutExplicit<Config>;

/** Factory input: only `omitFields` — remaining props get the auto-generated tab. */
export type DashboardLayoutOmitOnly<Config = unknown> = {
  omitFields?: readonly (DotPath<Config> | (string & {}))[];
  tabs?: never;
};

/** Factory input: explicit tabs — every config leaf must be listed or omitted. */
export type DashboardLayoutExplicit<Config = unknown> = {
  omitFields?: readonly (DotPath<Config> | (string & {}))[];
  tabs: readonly DashboardTab<Config>[];
};

type ExtractLayoutItemPath<Item> = Item extends string
  ? Item
  : Item extends { prop: infer P extends string }
    ? P
    : Item extends { descriptorControlField: infer D extends string }
      ? D
      : never;

type PathsFromFields<Fields extends readonly unknown[]> =
  ExtractLayoutItemPath<Fields[number]>;

type PathsFromFieldsets<
  Fieldsets extends readonly { fields: readonly unknown[] }[],
> = PathsFromFields<Fieldsets[number]['fields']>;

type PathsFromTabs<
  Tabs extends readonly { fieldsets: readonly { fields: readonly unknown[] }[] }[],
> = PathsFromFieldsets<Tabs[number]['fieldsets']>;

type SharedFieldPaths<SharedFields extends SharedFieldsDefinition> =
  (SharedFields['props'] extends readonly SharedFieldDefinition[]
    ? SharedFields['props'][number]['name']
    : never)
  | (SharedFields['descriptorControlFields'] extends readonly SharedFieldDefinition[]
    ? SharedFields['descriptorControlFields'][number]['name']
    : never);

/** Leaf paths that must appear in the dashboard layout (or omitFields). */
type ConfigLeafPaths<
  SharedFields extends SharedFieldsDefinition,
  Config,
> = [SharedFieldPaths<SharedFields>] extends [never]
  ? DotPath<Config>
  : SharedFieldPaths<SharedFields> | Exclude<DotPath<Config>, SharedFieldPaths<SharedFields>>;

type OmittedLayoutPaths<Layout> =
  Layout extends { omitFields?: infer OmitFields | undefined }
    ? OmitFields extends readonly string[]
      ? OmitFields[number]
      : never
    : never;

type CoveredLayoutPaths<
  Config,
  Layout extends { tabs: readonly DashboardTab<Config>[] },
> = PathsFromTabs<Layout['tabs']> | OmittedLayoutPaths<Layout>;

/** Config leaf paths neither placed in tabs nor listed in `omitFields`. */
export type MissingDashboardLayoutPaths<
  SharedFields extends SharedFieldsDefinition,
  Config,
  Layout extends { tabs: readonly DashboardTab<Config>[] },
> = Exclude<
  ConfigLeafPaths<SharedFields, Config>,
  CoveredLayoutPaths<Config, Layout>
>;

type ValidateDashboardLayoutFactoryResult<
  SharedFields extends SharedFieldsDefinition,
  Config,
  Layout extends DashboardLayoutFactoryInput<Config>,
> = Layout extends DashboardLayoutDisplayAll
  ? DashboardLayoutDisplayAll
  : Layout extends { tabs: readonly DashboardTab<Config>[] }
    ? ConfigLeafPaths<SharedFields, Config> extends CoveredLayoutPaths<
        Config,
        Layout
      >
      ? Layout
      : {
          __dashboardLayoutMissingPaths: MissingDashboardLayoutPaths<
            SharedFields,
            Config,
            Layout
          >;
        }
    : Layout extends DashboardLayoutOmitOnly<Config>
      ? Layout
      : Layout;

export type DashboardTab<Config = unknown> = {
  label: string;
  /** Stable tab id used as `activeTab` in scenario factories (defaults to snake_case label). */
  key?: string;
  fieldsets: readonly DashboardFieldset<Config>[];
};

export type DashboardFieldset<Config = unknown> = {
  legend: string;
  fields: readonly DashboardLayoutItem<Config>[];
};

/**
 * Declarative visibility conditions for the CB2 inspector.
 * Conditions are ANDed by default; `anyOf` provides OR groups and
 * `{ nonEmpty: true }` matches values that are not null, undefined, or blank
 * strings (`{ nonEmpty: false }` matches the opposite: missing or blank).
 *
 * Unknown configs keep an open string key set for generic consumers; inferred
 * block configs are restricted to their declared leaf paths.
 */
export type VisibleWhenValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | { readonly nonEmpty: boolean };

type VisibleWhenPath<Config> = unknown extends Config ? string : DotPath<Config>;

type VisibleWhenPropCondition<Config> = {
  readonly prop: VisibleWhenPath<Config>;
  readonly nonEmpty: true;
};

export type VisibleWhen<Config = unknown> =
  | { readonly [Key in VisibleWhenPath<Config>]?: VisibleWhenValue }
  | VisibleWhenPropCondition<Config>
  | { readonly anyOf: readonly VisibleWhen<Config>[] };

/**
 * A layout entry references a declared prop or descriptor control field by its
 * dotted path. `DotPath<Config>` narrows the path to an existing declared leaf
 * so a typo on a *declared* path is a compile error; the `(string & {})`
 * fallback accepts additional paths the author adds in the config factory
 * without re-declaring them in `sharedFields` (preserving authoring DX).
 *
 * Optional `visibleWhen` hides the field in the CB2 inspector when sibling
 * prop values do not match (strict equality, AND across keys).
 */
export type DashboardLayoutItem<Config = unknown> =
  | DotPath<Config>
  | (string & {})
  | {
      prop: DotPath<Config> | (string & {});
      label?: string;
      help?: string;
      visibleWhen?: VisibleWhen<Config>;
      /**
       * Inspector display width (CSS), e.g. `'50%'` to place two props on one row.
       * Defaults to full width when omitted.
       */
      width?: string;
    }
  | {
      descriptorControlField: DotPath<Config> | (string & {});
      label?: string;
      help?: string;
      visibleWhen?: VisibleWhen<Config>;
      /** Inspector display width (CSS), e.g. `'50%'`. */
      width?: string;
    };

/** Minimal observable-like shape — avoids a runtime rxjs dependency in this library. */
type ObservableLike<T> = {
  subscribe(observer: (value: T) => void): unknown;
};

export type ObservableValue<T> = T extends ObservableLike<infer Value> ? Value : never;

export type ScenarioContentOf<TDataSource extends { vm$: ObservableLike<unknown> }> =
  ObservableValue<TDataSource['vm$']>;

export type DoodBlockScenarioContext = {
  /** Dashboard layout tab key currently edited in CB2 Studio. */
  activeTab?: string | null;
  /** Live bloc/modal props used to refine scenario auto-selection. */
  props?: Record<string, unknown>;
};

export type DoodBlockScenarioFactory<TContent = unknown> = (
  ctx: DoodBlockScenarioContext,
) => DoodBlockScenarioMatrix<TContent>;

export type DoodBlockScenario<TContent = unknown> = {
  /** i18n key displayed in the CB2 preview scenario selector and used as selection identity. */
  name: string;
  /** Short English note for developers reading scenario definitions in source. */
  _devDescription: string;
  /** i18n key explaining when this scenario applies (optional tooltip/docs). */
  when?: string;
  /** When true, this scenario is pre-selected for its dimension in CB2 preview. */
  default?: boolean;
  /** Layer merged into the preview VM (full state or partial overlay). */
  content?: Partial<TContent>;
  includes?: readonly DoodBlockScenario<TContent>[];
  /** When set, scenario is auto-selected when this predicate returns true. */
  activeWhen?: () => boolean;
};

export type ScenarioDescription<TContent = unknown> = Omit<DoodBlockScenario<TContent>, 'name'>;

export type ScenarioBuilder = {
  describe: <TContent = unknown>(
    name: string,
    scenario: ScenarioDescription<TContent>,
  ) => DoodBlockScenario<TContent>;
};

type ComponentTypePrefix<ComponentType extends string> =
  ComponentType extends `${infer Base}Component`
    ? Uncapitalize<Base>
    : Uncapitalize<ComponentType>;

type StripComponentSuffix<T extends string> =
  T extends `${infer Base}Component` ? Base : T;

type StripAtomBlockSuffix<T extends string> =
  T extends `${infer Base}Atom`
    ? Base
    : T extends `${infer Base}Block`
      ? Base
      : T;

export type ComposeConfigExportName<T extends string> =
  `compose${Capitalize<Uncapitalize<StripAtomBlockSuffix<StripComponentSuffix<T>>>>}Config`;

export type ComposePropertyHelper<Type extends DoodFieldType> = (
  overrides?: Partial<
    Omit<PropertyValueDescriptor<Type>, 'type'> & {
      conditions?: readonly ConditionalPropertyValue<
        import('./descriptor').FieldValueByType<Type> | ParamRef | CasesValue
      >[];
    }
  >,
) => PropertyValue<Type>;

export type ComposePropertyHelpers<Config> = {
  [K in keyof Config]: Config[K] extends PropertyValue<infer Type>
    ? ComposePropertyHelper<Type>
    : Config[K] extends { [key: string]: unknown }
      ? ComposePropertyHelpers<Config[K]>
      : never;
};

type OmitComposeKeys<
  Config,
  Keys extends readonly string[],
> = Omit<Config, Keys[number] & keyof Config>;

export type ComposeConfigWithOmitFn<
  Config extends Record<string, unknown>,
  K extends readonly string[],
> = {
  (): OmitComposeKeys<Config, K>;
  (
    callback: (
      helpers: ComposePropertyHelpers<OmitComposeKeys<Config, K>>,
    ) => Partial<Config>,
  ): OmitComposeKeys<Config, K>;
};

export type ComposeAliasProperties<Config extends Record<string, unknown>> = {
  [K in keyof Config]?: string;
};

export type ComposeAliasOptions<Config extends Record<string, unknown>> = {
  properties: ComposeAliasProperties<Config>;
};

export type ApplyComposeKeyAliases<
  Config extends Record<string, unknown>,
  Aliases extends ComposeAliasProperties<Config>,
> = [keyof Aliases] extends [never]
  ? Config
  : Omit<Config, Extract<keyof Aliases, keyof Config>> & {
      [Canonical in keyof Aliases & keyof Config as NonNullable<
        Aliases[Canonical]
      >]: Config[Canonical];
    };

export type ComposeConfigFn<
  Config extends Record<string, unknown>,
  Aliases extends ComposeAliasProperties<Config> = ComposeAliasProperties<Config>,
> = {
  (): ApplyComposeKeyAliases<Config, Aliases>;
  (
    callback: (
      helpers: ComposePropertyHelpers<ApplyComposeKeyAliases<Config, Aliases>>,
    ) => Partial<ApplyComposeKeyAliases<Config, Aliases>>,
  ): ApplyComposeKeyAliases<Config, Aliases>;
  withOmit: <const K extends readonly (keyof Config & string)[]>(
    omit: K,
  ) => ComposeConfigFn<Config, Aliases>;
  withAlias: <const M extends ComposeAliasProperties<Config>>(
    options: { properties: M },
  ) => ComposeConfigFn<Config, Aliases & M>;
  withRename: <const M extends ComposeAliasProperties<Config>>(
    map: M,
  ) => ComposeConfigFn<Config, Aliases & M>;
  composeAliases: Readonly<Record<string, keyof Config & string>>;
};

export type PrefixedDoodBlockDefinition<
  Config,
  ComponentType extends string,
> = {
  [Key in `${ComponentTypePrefix<ComponentType>}Descriptor`]: IBlockDescriptor;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}BlocDefinition`]: BlocDefinition;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Config`]: ResolvedConfig<Config>;
} & {
  [Key in ComposeConfigExportName<ComponentType>]: ComposeConfigFn<
    Config & Record<string, unknown>,
    {}
  >;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Properties`]: Config;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}DashboardLayout`]: DashboardLayout<Config>;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}Scenarios`]: DoodBlockScenarioFactory;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}DefinitionSetupScenarios`]: DefinitionSetupScenariosFn<ComponentType>;
} & {
  [Key in `${ComponentTypePrefix<ComponentType>}ResolveConfig`]: (
    input: ResolveBlockConfigRuntimeInput<ResolvedConfig<Config>>,
  ) => ResolvedConfig<Config>;
};

/**
 * A config node is either a `PropertyValue` leaf, a `DescriptorControlField`
 * leaf, or a nested grouping. Only `PropertyValue` leaves end up in the webapp
 * config / override config; `DescriptorControlField` leaves are dashboard-only.
 */
export type ConfigNode =
  | PropertyValue<DoodFieldType>
  | DescriptorControlField<DoodFieldType>
  | { [key: string]: ConfigNode };

export type ResolvedConfig<Config> = {
  [K in keyof Config]: Config[K] extends PropertyValue<infer Type>
    ? import('./descriptor').FieldValueByType<Type>
    : Config[K] extends DescriptorControlField<any>
      ? never
      : Config[K] extends { [key: string]: unknown }
        ? ResolvedConfig<Config[K]>
        : Config[K];
};

export type OverrideConfig<Config> = {
  [K in keyof Config]?: Config[K] extends PropertyValue<infer Type>
    ? import('./descriptor').FieldValueByType<Type> | undefined
    : Config[K] extends DescriptorControlField<any>
      ? never
      : Config[K] extends { [key: string]: unknown }
        ? OverrideConfig<Config[K]>
        : Config[K];
};

/* -------------------------------------------------------------------------- */
/* propertyValue / descriptorControlField factories                           */
/* -------------------------------------------------------------------------- */

export function propertyValue<
  Type extends DoodFieldType,
  Value = import('./descriptor').FieldValueByType<Type>,
>(
  descriptor: PropertyValueDescriptor<Type, Value>,
): PropertyValue<Type>;
export function propertyValue<
  Type extends DoodFieldType,
  Value = import('./descriptor').FieldValueByType<Type>,
>(
  descriptor: {
    type: Type;
    description?: string;
    options?: Record<string, unknown>;
    allowedRoles?: readonly string[];
    dashboardVisibilityWhen?: PropertyValueDashboardVisibilityPredicate;
  },
  conditions: readonly ConditionalPropertyValue<
    Value | ParamRef | CasesValue
  >[],
): PropertyValue<Type>;
export function propertyValue(descriptor: any, conditions?: any): PropertyValue<any> {
  if (conditions !== undefined) {
    return {
      [PROPERTY_VALUE_BRAND]: true as const,
      kind: 'conditional',
      type: descriptor.type,
      conditions,
      description: descriptor.description,
      options: descriptor.options,
      allowedRoles: descriptor.allowedRoles,
      dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
    } as unknown as PropertyValue<any>;
  }
  return {
    [PROPERTY_VALUE_BRAND]: true as const,
    kind: 'static',
    type: descriptor.type,
    value: descriptor.value,
    description: descriptor.description,
    options: descriptor.options,
    allowedRoles: descriptor.allowedRoles,
    dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
  } as unknown as PropertyValue<any>;
}

export function descriptorControlField<Type extends DoodFieldType>(
  descriptor: {
    type: Type;
    value?: import('./descriptor').FieldValueByType<Type>;
    description?: string;
    options?: Record<string, unknown>;
    dashboardVisibilityWhen?: PropertyValueDashboardVisibilityPredicate;
  },
): DescriptorControlField<Type> {
  return {
    [DESCRIPTOR_CONTROL_FIELD_BRAND]: true as const,
    type: descriptor.type,
    value: descriptor.value,
    description: descriptor.description,
    options: descriptor.options,
    dashboardVisibilityWhen: descriptor.dashboardVisibilityWhen,
  } as unknown as DescriptorControlField<Type>;
}

export function isPropertyValue(
  node: unknown,
): node is PropertyValue<DoodFieldType> {
  return (
    typeof node === 'object' &&
    node !== null &&
    (node as any)[PROPERTY_VALUE_BRAND] === true
  );
}

export function isDescriptorControlField(
  node: unknown,
): node is DescriptorControlField<DoodFieldType> {
  return (
    typeof node === 'object' &&
    node !== null &&
    (node as any)[DESCRIPTOR_CONTROL_FIELD_BRAND] === true
  );
}

export { paramRef };

/* -------------------------------------------------------------------------- */
/* Block condition evaluation (declarative, webapp-side)                      */
/* -------------------------------------------------------------------------- */

export function evaluateBlockCondition(
  condition: BlockCondition,
  context: BlockConditionResolveInput['context'],
): boolean {
  const opts = condition.options as Record<string, unknown>;
  switch (condition.type) {
    case 'locale':
      return typeof context.locale === 'string' && (opts.in as readonly string[]).includes(context.locale);
    case 'device':
      return typeof context.device === 'string' && (opts.in as readonly string[]).includes(context.device);
    case 'userAuth':
      return context.userAuth === (opts as { visibility: string }).visibility;
    case 'timeOfDay': {
      if (!context.timeOfDay) return false;
      return context.timeOfDay.now >= (opts as { start: string }).start &&
        context.timeOfDay.now < (opts as { end: string }).end;
    }
    case 'weekday': {
      if (!context.weekday) return false;
      return (opts as { selectedDays: readonly number[] }).selectedDays.includes(
        context.weekday.now.getDay(),
      );
    }
    case 'dateRange': {
      if (!context.dateRange) return false;
      const now = context.dateRange.now.toISOString();
      return now >= (opts as { start: string }).start && now < (opts as { end: string }).end;
    }
    case 'journeyParameter': {
      if (!context.journeyParameters) return false;
      const key = (opts as { paramKey: string }).paramKey;
      const actual = context.journeyParameters[key];
      const operator = (opts as { operator: string }).operator;
      switch (operator) {
        case 'eq':
        case 'equals':
          return actual === (opts as { value?: unknown }).value;
        case 'neq':
        case 'notEquals':
          return actual !== (opts as { value?: unknown }).value;
        case 'defined':
        case 'exists':
          return actual != null && actual !== '';
        case 'notDefined':
          return actual == null || actual === '';
        case 'isTrue':
          return actual === true;
        case 'isFalse':
          return actual === false;
        case 'truthy':
          return Boolean(actual);
        case 'falsy':
          return !Boolean(actual);
        default:
          return false;
      }
    }
    case 'loyaltyMember':
      return context.isLoyaltyMember === Boolean(opts.value ?? true);
    case 'userTag': {
      const label = typeof opts.label === 'string' ? opts.label : '';
      const hasTag =
        label.length > 0 &&
        Array.isArray(context.userTags) &&
        context.userTags.includes(label);
      return opts.condition === 'hasNot' ? !hasTag : hasTag;
    }
    case 'testMode': {
      const inTestMode = context.isTestMode === true;
      return opts.visibility === 'notInTestMode' ? !inTestMode : inTestMode;
    }
    case 'cartType':
      return context.cartType === (opts as { value?: string }).value;
    case 'shop':
    case 'userField':
    case 'customExpert':
      // Domain-specific; left to the webapp to implement. Conservative default.
      return false;
    default:
      return false;
  }
}

export type BlockConditionResolveInput = {
  context: import('./property-value').BlockContext;
};

/* -------------------------------------------------------------------------- */
/* Runtime resolution                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Resolves a `PropertyValue` against a runtime `BlockContext`: static values
 * are returned as-is; conditional values pick the first matching condition.
 * The legacy JS resolver has been removed — conditions are declarative and
 * evaluated via `evaluateBlockCondition`.
 */
export function resolvePropertyValue<Type extends DoodFieldType>(
  property: PropertyValue<Type>,
  context: import('./property-value').BlockContext,
): import('./descriptor').FieldValueByType<Type> {
  if (property.kind === 'static' || property.kind === undefined) {
    return property.value as import('./descriptor').FieldValueByType<Type>;
  }
  if (property.kind === 'conditional' && property.conditions) {
    for (const candidate of property.conditions) {
      if (evaluateBlockCondition(candidate.condition, context)) {
        return candidate.value as import('./descriptor').FieldValueByType<Type>;
      }
    }
  }
  return undefined as import('./descriptor').FieldValueByType<Type>;
}

/* -------------------------------------------------------------------------- */
/* Descriptor generation (CB1)                                                */
/* -------------------------------------------------------------------------- */

type FieldOptionsForType<Type extends DoodFieldType> =
  Type extends 'text'
    ? import('./descriptor').TextFieldOptions
    : Type extends 'link'
      ? import('./descriptor').LinkFieldOptions
    : Type extends 'checkbox'
      ? import('./descriptor').CheckboxFieldOptions
      : Type extends 'color'
        ? import('./descriptor').ColorFieldOptions
        : Type extends 'image_file'
          ? import('./descriptor').ImageFileFieldOptions
          : Record<string, unknown>;

function buildFieldOptions<Type extends DoodFieldType>(
  _type: Type,
  path: string,
  options: Record<string, unknown> | undefined,
): FieldOptionsForType<Type> {
  const label =
    options && typeof options.label === 'string' ? options.label : path;
  const merged = { ...(options ?? {}), label } as Record<string, unknown>;
  // CB2 stores select/radio items under `choices`; CB1 templates read nested `options`.
  if (
    (_type === 'select' || _type === 'radio') &&
    Array.isArray(merged['choices']) &&
    !Array.isArray(merged['options'])
  ) {
    merged['options'] = merged['choices'];
  }
  return merged as FieldOptionsForType<Type>;
}

/**
 * Walks a config node tree and emits one legacy `DoodBlockField` per
 * `propertyValue` leaf. Paths are split on `.`: intermediate segments become
 * `groupedFields` wrappers (fieldset) so nested props like `colors.textColor`
 * land in a `colors` group with handle `text_color`.
 */
/**
 * Legacy CB1 field type a `DoodFieldType` projects to in the CB1 descriptor.
 * CB2-only prop-types (`string`, `url`, `slider`) are mapped to the closest
 * legacy field type so the emitted `DoodBlockField` stays assignable to the
 * dashboard `BlockFields` union. `image` is normalized to `image_file`.
 */
function toCb1FieldType<Type extends DoodFieldType>(type: Type): DoodFieldType {
  switch (type) {
    case 'string':
    case 'url':
      return 'text';
    case 'link':
      return 'link';
    case 'image':
      return 'image_file';
    case 'slider':
      return 'number';
    case 'unit_value':
      return 'text';
    case 'shop':
      return 'shop_picker';
    default:
      return type;
  }
}

function collectFields(
  node: unknown,
  path: string,
  out: LegacyBlockField[],
  omit: ReadonlySet<string>,
): void {
  if (isPropertyValue(node)) {
    if (path && omit.has(path)) {
      return;
    }
    // Only the last path segment is snake_cased into the handle, matching the
    // legacy dashboard convention (`colors.backgroundColor` -> `background_color`).
    // Intermediate segments surface as `groupedFields` fieldset wrappers.
    const handle = toSnakeHandle(lastSegment(path));
    if (node.type === 'spacing_box') {
      out.push(expandSpacingBoxToGroupedFields(handle, node));
      return;
    }
    const cb1Type = toCb1FieldType(node.type);
    const options = buildFieldOptions(cb1Type, path, node.options as any) as Record<
      string,
      unknown
    >;
    backfillLegacyFieldDefault(cb1Type, node.value, options);
    out.push({
      handle,
      type: cb1Type,
      options,
      ...legacyAllowedRoles(node.allowedRoles),
    } as DoodBlockField);
    return;
  }
  if (isDescriptorControlField(node)) {
    // Descriptor control fields do not surface in the legacy webapp descriptor.
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectFields(child, path ? `${path}.${key}` : key, out, omit);
    }
  }
}

/** CB2 `allowedIn.bloc` uses glob `*`; CB1 `allowed_in` is a full-string regex. */
function toLegacyAllowedInPattern(bloc: string): string {
  return bloc.replace(/Component$/, '').replace(/\*/g, '.*');
}

/** CB1 picker keys omit the `Component` suffix (`MosaicCardBlockComponent` → `MosaicCardBlock`). */
function toLegacyAllowedBlockType(type: string): string {
  return type.replace(/Component$/, '');
}

function buildBlockDescriptor<
  SharedFields extends SharedFieldsDefinition,
  Name extends string,
  Config,
>(
  metadata: DoodBlockMetadata<SharedFields, Name>,
  config: Config,
  omitFields?: readonly string[],
): IBlockDescriptor {
  const {
    category,
    keywords,
    sharedFields,
    componentType,
    description,
    slots,
    isRootBlock,
    isModalRoot,
    hideFromPicker,
    allowedIn,
    ...rest
  } = metadata;
  void category;
  void keywords;
  void sharedFields;
  void componentType;
  void description;
  void isRootBlock;
  void isModalRoot;
  void hideFromPicker;
  void allowedIn;

  const fields: LegacyBlockField[] = [];
  collectFields(config, '', fields, new Set(omitFields ?? []));

  // Project CB2 slots to legacy `block_collection` fields so the existing
  // dashboard/webapp runtime can ingest them during the transition. Regex
  // `allowed_block_types` are derived from explicit `allowedTypes` only —
  // legacy regexes are NOT invented (guide Annexe D).
  for (const slot of slots ?? []) {
    const allowedBlockTypes =
      slot.accept === 'all' || !slot.allowedTypes || slot.allowedTypes.length === 0
        ? ['.*']
        : slot.allowedTypes.map(toLegacyAllowedBlockType);
    fields.push({
      handle: toSnakeHandle(slot.key),
      type: 'block_collection',
      options: {
        label: typeof slot.name === 'string' ? slot.name : slot.key,
        allowed_block_types: allowedBlockTypes,
        ...(slot.max === undefined ? {} : { max: slot.max }),
        ...(slot.defaultBlocks?.length
          ? { default_blocks: slot.defaultBlocks.map(defaultBlock => defaultBlock.id) }
          : {}),
      },
      ...legacyAllowedRoles(slot.allowedRoles),
    } as DoodBlockField);
  }

  const descriptor: IBlockDescriptor = {
    ...rest,
    fields,
  };
  // Prefer explicit legacy `allowed_in`. Otherwise project CB2 `allowedIn`
  // parents to legacy names (ShopPageComponent → ShopPage).
  if (!descriptor.allowed_in?.length && allowedIn?.length) {
    descriptor.allowed_in = [
      ...new Set(allowedIn.map(entry => toLegacyAllowedInPattern(entry.bloc))),
    ];
  }
  return descriptor;
}

export function buildResolvedConfig<Config>(config: Config): ResolvedConfig<Config> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (isPropertyValue(value)) {
      out[key] = extractStaticValue(value as PropertyValue<DoodFieldType>);
      continue;
    }
    if (isDescriptorControlField(value)) {
      continue;
    }
    if (value && typeof value === 'object') {
      const nested = buildResolvedConfig(value);
      out[key] = nested;
    } else {
      out[key] = value;
    }
  }
  return out as ResolvedConfig<Config>;
}

export function buildOverrideConfig<Config>(config: Config): OverrideConfig<Config> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (isPropertyValue(value)) {
      // Omitted by default; override config is a partial skeleton.
      continue;
    }
    if (isDescriptorControlField(value)) {
      continue;
    }
    if (value && typeof value === 'object') {
      out[key] = buildOverrideConfig(value);
    }
  }
  return out as OverrideConfig<Config>;
}

/**
 * Extracts the serializable static value of a `PropertyValue` for the resolved
 * config snapshot. `$param`/`$cases` references are passed through as-is; the
 * conditional `kind` resolves to `undefined` without a runtime context.
 */
function extractStaticValue(
  property: PropertyValue<DoodFieldType>,
): unknown {
  if (property.kind === 'static') {
    return property.value;
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* CB2 projection (buildBlocDefinition)                                       */
/* -------------------------------------------------------------------------- */

const LEGACY_TO_CB2_TYPE: {
  [K in DoodFieldType]?: Cb2PropertyTypeFor<K>;
} = {
  text: 'string' as const,
  string: 'string' as const,
  textarea: 'textarea' as const,
  translation: 'string' as const,
  wysiwyg: 'wysiwyg' as const,
  url: 'url' as const,
  link: 'link' as const,
  select: 'select' as const,
  radio: 'radio' as const,
  checkbox: 'checkbox' as const,
  number: 'number' as const,
  slider: 'number' as const,
  color: 'color' as const,
  image: 'image' as const,
  image_file: 'image' as const,
  video_file: 'video' as const,
  spacing_box: 'spacing_box' as const,
  unit_value: 'unit_value' as const,
  shop: 'shop' as const,
  suggestions_set_picker: 'suggestions_set' as const,
};

function toCb2PropertyType<Type extends DoodFieldType>(
  type: Type,
): import('./bloc-definition').BlocPropertyType {
  return (LEGACY_TO_CB2_TYPE[type] ?? type) as import('./bloc-definition').BlocPropertyType;
}

/**
 * Converts a declarative `BlockCondition` to a serializable `$cases` payload.
 * The condition's `type` becomes the `dimension`; `options` becomes the `when`
 * predicate payload; the fallback `default` is the next branch or the provided
 * fallback.
 */
function conditionToCases(
  conditions: readonly ConditionalPropertyValue<unknown>[],
  fallback: SerializableValue,
  propertyType?: DoodFieldType,
): CasesValue | null {
  if (conditions.length === 0) return null;
  const first = conditions[0];
  if (!first) return null;
  const branches = conditions.map(branch => ({
    when: { ...branch.condition.options, $kind: branch.condition.type },
    value: toPropertySerializable(propertyType, branch.value),
  }));
  return {
    $cases: {
      dimension: first.condition.type,
      branches,
      default: fallback,
    },
  };
}

function toPropertySerializable(
  propertyType: DoodFieldType | undefined,
  value: unknown,
): SerializableValue {
  if (propertyType === 'color') {
    const paletteParamRef = paletteColorValueToParamRef(value);
    if (paletteParamRef) {
      return paletteParamRef;
    }
  }

  return toSerializable(value);
}

function toSerializable(value: unknown): SerializableValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object') {
    if ('$param' in (value as Record<string, unknown>)) {
      return { $param: (value as ParamRef).$param } as ParamRef;
    }
    if ('$cases' in (value as Record<string, unknown>)) {
      return value as CasesValue;
    }
    if (isPropertyValue(value)) {
      return propertyValueToSerializable(value as PropertyValue<DoodFieldType>);
    }
    // Translation field -> $cases(locale).
    if (isTranslationField(value)) {
      return translationToCases(value);
    }
    return JSON.parse(JSON.stringify(value)) as SerializableValue;
  }
  return JSON.parse(JSON.stringify(value)) as SerializableValue;
}

const CONTENT_BUILDER_COLOR_TYPES = new Set(['hex', 'palette', 'default']);

/** Legacy color field `{ type, value }` — must not be projected as a translation map. */
function isContentBuilderColorField(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const obj = value as Record<string, unknown>;
  const type = obj['type'];
  if (typeof type !== 'string' || !CONTENT_BUILDER_COLOR_TYPES.has(type)) {
    return false;
  }
  const colorValue = obj['value'];
  return (
    typeof colorValue === 'string' ||
    colorValue === null ||
    colorValue === undefined
  );
}

function isTranslationField(value: unknown): value is TranslationField {
  if (typeof value !== 'object' || value === null) return false;
  if (isContentBuilderColorField(value)) return false;
  const entries = Object.entries(value as Record<string, unknown>);
  return entries.length > 0 && entries.every(([, v]) => typeof v === 'string');
}

/**
 * Maps a legacy multi-locale `TranslationField` to a CB2 `$cases` on the
 * `locale` dimension (one branch per locale, `default` = first locale). Keeps
 * the multi-locale semantics while staying serializable in CB2.
 */
function translationToCases(translation: TranslationField): CasesValue {
  const entries = Object.entries(translation);
  const branches = entries.map(([locale, text]) => ({
    when: { in: [locale] },
    value: text as SerializableValue,
  }));
  const fallback = (entries[0]?.[1] ?? '') as SerializableValue;
  return {
    $cases: {
      dimension: 'locale',
      branches,
      default: fallback,
    },
  };
}

function propertyValueToSerializable(
  property: PropertyValue<DoodFieldType>,
): SerializableValue {
  if (property.kind === 'static') {
    return toPropertySerializable(property.type, property.value);
  }
  if (property.kind === 'conditional' && property.conditions) {
    const cases = conditionToCases(
      property.conditions as readonly ConditionalPropertyValue<unknown>[],
      null,
      property.type,
    );
    return cases ?? null;
  }
  return null;
}

/**
 * Walks the config tree and produces CB2 `BlocPropertyGroup`s. Intermediate
 * nesting segments become groups keyed by their snake_case `sectionId`.
 */
function collectPropertyGroups(
  node: unknown,
  path: string,
  groups: Map<string, BlocProperty[]>,
): void {
  if (isPropertyValue(node)) {
    const sectionId = path.includes('.')
      ? toSnakeHandle(path.slice(0, path.lastIndexOf('.'))) || 'general'
      : 'general';
    const key = toSnakeHandle(lastSegment(path));
    const label =
      (node.options && typeof node.options.label === 'string'
        ? node.options.label
        : key) as import('./bloc-definition').I18nLabel;
    const property: BlocProperty = {
      key,
      type: toCb2PropertyType(node.type),
      label,
      options: stripLabel(node.options),
    };
    if (node.options?.["required"] === true) {
      property.required = true;
    }
    if (node.description) {
      property.description = node.description;
    }
    if (node.kind === 'static' && node.value !== undefined) {
      property.default = toPropertySerializable(node.type, node.value);
    } else if (node.kind === 'conditional' && node.conditions) {
      property.default = conditionToCases(
        node.conditions as readonly ConditionalPropertyValue<unknown>[],
        null,
        node.type,
      ) ?? undefined;
    }
    pushProperty(groups, sectionId, property);
    return;
  }
  if (isDescriptorControlField(node)) {
    // Descriptor control fields are dashboard-only and do not exist in CB2.
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectPropertyGroups(child, path ? `${path}.${key}` : key, groups);
    }
  }
}

function stripLabel(
  options: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!options) return undefined;
  const { label: _label, ...rest } = options;
  return Object.keys(rest).length === 0 ? undefined : rest;
}

function pushProperty(
  groups: Map<string, BlocProperty[]>,
  sectionId: string,
  property: BlocProperty,
): void {
  const list = groups.get(sectionId);
  if (list) {
    list.push(property);
  } else {
    groups.set(sectionId, [property]);
  }
}

function buildBlocDefinition<
  SharedFields extends SharedFieldsDefinition,
  Name extends string,
  Config,
>(
  metadata: DoodBlockMetadata<SharedFields, Name>,
  config: Config,
): BlocDefinition {
  const groups = new Map<string, BlocProperty[]>();
  collectPropertyGroups(config, '', groups);

  const propertyGroups: BlocPropertyGroup[] = [];
  for (const [sectionId, properties] of groups) {
    propertyGroups.push({
      sectionId,
      label: sectionId,
      properties,
    });
  }

  const definition: BlocDefinition = {
    type: metadata.componentType,
    name: metadata.name,
    category: metadata.category,
    propertyGroups,
  };
  if (metadata.description) definition.description = metadata.description;
  if (metadata.keywords && metadata.keywords.length > 0) {
    definition.keywords = metadata.keywords;
  }
  if (metadata.slots && metadata.slots.length > 0) {
    definition.slots = metadata.slots;
  }
  if (metadata.allowedIn && metadata.allowedIn.length > 0) {
    definition.allowedIn = metadata.allowedIn;
  }
  if (metadata.isRootBlock) definition.isRootBlock = true;
  if (metadata.isModalRoot) definition.isModalRoot = true;
  if (metadata.hideFromPicker) definition.hideFromPicker = true;
  if (metadata.creationDefaults) definition.creationDefaults = metadata.creationDefaults;
  return definition;
}

/* -------------------------------------------------------------------------- */
/* Default dashboard layout (all props minus omitFields)                      */
/* -------------------------------------------------------------------------- */

function collectLeafPaths(
  node: unknown,
  path: string,
  out: string[],
): void {
  if (isPropertyValue(node) || isDescriptorControlField(node)) {
    out.push(path);
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      collectLeafPaths(child, path ? `${path}.${key}` : key, out);
    }
  }
}

function resolveDashboardLayout<Config>(
  layout: DashboardLayoutFactoryInput<Config>,
  config: Config,
): DashboardLayout<Config> {
  if (layout === 'displayAll') {
    return buildDefaultDashboardLayout(config, undefined);
  }
  if (layout.tabs) return layout;
  return buildDefaultDashboardLayout(config, layout.omitFields);
}

function buildDefaultDashboardLayout<Config>(
  config: Config,
  omitFields: readonly string[] | undefined,
): DashboardLayout<Config> {
  const leafPaths: string[] = [];
  collectLeafPaths(config, '', leafPaths);
  const omit = new Set(omitFields ?? []);
  const ordered = leafPaths;
  const kept = ordered.filter(p => !omit.has(p));
  if (kept.length === 0) {
    return { omitFields, tabs: [] };
  }
  return {
    omitFields,
    tabs: [
      {
        label: 'Design',
        fieldsets: [
          {
            legend: 'Properties',
            fields: kept as unknown as readonly DashboardLayoutItem<Config>[],
          },
        ],
      },
    ],
  };
}

/* -------------------------------------------------------------------------- */
/* Config composition (host override)                                         */
/* -------------------------------------------------------------------------- */

function makeComposeHelper<Type extends DoodFieldType>(
  hostLeaf: PropertyValue<Type>,
): ComposePropertyHelper<Type> {
  return overrides => {
    if (!overrides) {
      return { ...hostLeaf };
    }
    const merged = {
      ...hostLeaf,
      ...overrides,
      type: hostLeaf.type,
      options: { ...hostLeaf.options, ...overrides.options },
    } as PropertyValue<Type>;
    if (overrides.conditions !== undefined) {
      merged.kind = 'conditional';
      merged.conditions = overrides.conditions;
    } else if (overrides.value !== undefined) {
      merged.kind = 'static';
      merged.value = overrides.value;
    }
    return merged;
  };
}

function buildComposePropertyHelpers<Config>(
  node: unknown,
): ComposePropertyHelpers<Config> {
  if (isPropertyValue(node)) {
    return makeComposeHelper(node) as unknown as ComposePropertyHelpers<Config>;
  }
  if (isDescriptorControlField(node)) {
    return undefined as unknown as ComposePropertyHelpers<Config>;
  }
  if (node && typeof node === 'object') {
    const helpers: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(node as Record<string, unknown>)) {
      const helper = buildComposePropertyHelpers(child);
      if (helper !== undefined) {
        helpers[key] = helper;
      }
    }
    return helpers as ComposePropertyHelpers<Config>;
  }
  return undefined as unknown as ComposePropertyHelpers<Config>;
}

export function cloneConfigTree<Config>(config: Config): Config {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (isPropertyValue(value) || isDescriptorControlField(value)) {
      out[key] = { ...value };
      continue;
    }
    if (value && typeof value === 'object') {
      out[key] = cloneConfigTree(value);
      continue;
    }
    out[key] = value;
  }
  return out as Config;
}

export function deepMergeConfigNodes(
  base: ConfigNode,
  overrides: ConfigNode | undefined,
): ConfigNode {
  if (!overrides) {
    return base;
  }
  if (isPropertyValue(base) || isDescriptorControlField(base)) {
    return overrides;
  }
  if (isPropertyValue(overrides) || isDescriptorControlField(overrides)) {
    return overrides;
  }
  const baseRecord = base as Record<string, ConfigNode>;
  const overrideRecord = overrides as Record<string, ConfigNode>;
  const merged: Record<string, ConfigNode> = { ...baseRecord };
  for (const [key, overrideChild] of Object.entries(overrideRecord)) {
    if (overrideChild === undefined) {
      continue;
    }
    const baseChild = baseRecord[key];
    merged[key] =
      baseChild !== undefined
        ? deepMergeConfigNodes(baseChild, overrideChild)
        : overrideChild;
  }
  return merged;
}

function applyOmit<Config>(
  config: Config,
  omit: readonly string[] | undefined,
): Config {
  if (!omit?.length) {
    return config;
  }
  const omitSet = new Set(omit);
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (omitSet.has(key)) {
      continue;
    }
    out[key] = value;
  }
  return out as Config;
}

type ComposePipelineState<
  Config extends Record<string, unknown>,
  Aliases extends ComposeAliasProperties<Config> = ComposeAliasProperties<Config>,
> = {
  host: Config;
  omit: readonly string[];
  aliases: Aliases;
};

function buildComposeAliasesInverse<Config extends Record<string, unknown>>(
  aliases: ComposeAliasProperties<Config>,
): Readonly<Record<string, keyof Config & string>> {
  const inverse: Record<string, keyof Config & string> = {};
  for (const [canonical, alias] of Object.entries(aliases)) {
    if (alias) {
      inverse[alias] = canonical as keyof Config & string;
    }
  }
  return inverse;
}

function applyAliasToConfigTree<Config extends Record<string, unknown>>(
  config: Config,
  aliases: ComposeAliasProperties<Config>,
): Config {
  const out: Record<string, unknown> = { ...(config as Record<string, unknown>) };

  for (const [canonical, alias] of Object.entries(aliases)) {
    if (!alias || !(canonical in out)) {
      continue;
    }
    out[alias] = cloneConfigTree(out[canonical]);
    markComposeAliasCanonical(out[alias], canonical);
    delete out[canonical];
  }

  return out as Config;
}

function applyComposeAliasMarks(
  config: Record<string, unknown>,
  aliases: ComposeAliasProperties<Record<string, unknown>>,
): void {
  for (const [canonical, alias] of Object.entries(aliases)) {
    if (!alias) {
      continue;
    }
    const node = config[alias];
    if (node !== undefined) {
      markComposeAliasCanonical(node, canonical);
    }
  }
}

function runComposePipeline<
  Config extends Record<string, unknown>,
  Aliases extends ComposeAliasProperties<Config> = ComposeAliasProperties<Config>,
>(
  state: ComposePipelineState<Config, Aliases>,
  callback?: (
    helpers: ComposePropertyHelpers<ApplyComposeKeyAliases<Config, Aliases>>,
  ) => Partial<ApplyComposeKeyAliases<Config, Aliases>>,
): ApplyComposeKeyAliases<Config, Aliases> {
  let base = cloneConfigTree(state.host);
  base = applyOmit(base, state.omit);
  base = applyAliasToConfigTree(base, state.aliases);
  applyComposeAliasMarks(
    base as Record<string, unknown>,
    state.aliases as ComposeAliasProperties<Record<string, unknown>>,
  );

  if (!callback) {
    return base as ApplyComposeKeyAliases<Config, Aliases>;
  }

  const helpers = buildComposePropertyHelpers<ApplyComposeKeyAliases<Config, Aliases>>(base);
  const overrides = callback(helpers);
  const merged = deepMergeConfigNodes(base as ConfigNode, overrides as ConfigNode) as ApplyComposeKeyAliases<
    Config,
    Aliases
  >;
  applyComposeAliasMarks(
    merged as Record<string, unknown>,
    state.aliases as ComposeAliasProperties<Record<string, unknown>>,
  );
  return merged;
}

function createComposeFnFromState<
  Config extends Record<string, unknown>,
  Aliases extends ComposeAliasProperties<Config> = ComposeAliasProperties<Config>,
>(
  state: ComposePipelineState<Config, Aliases>,
): ComposeConfigFn<Config, Aliases> {
  const compose = ((callback?: (
    helpers: ComposePropertyHelpers<ApplyComposeKeyAliases<Config, Aliases>>,
  ) => Partial<ApplyComposeKeyAliases<Config, Aliases>>) =>
    runComposePipeline(state, callback)) as ComposeConfigFn<Config, Aliases>;

  compose.withOmit = <const K extends readonly (keyof Config & string)[]>(omit: K) =>
    createComposeFnFromState<Config, Aliases>({
      ...state,
      omit: [...state.omit, ...omit],
    });

  compose.withAlias = <const M extends ComposeAliasProperties<Config>>(options: {
    properties: M;
  }) =>
    createComposeFnFromState<Config, Aliases & M>({
      ...state,
      aliases: { ...state.aliases, ...options.properties } as Aliases & M,
    });

  compose.withRename = <const M extends ComposeAliasProperties<Config>>(map: M) =>
    compose.withAlias({ properties: map });

  compose.composeAliases = buildComposeAliasesInverse(state.aliases);

  return compose;
}

export function createComposeConfigFn<Config extends Record<string, unknown>>(
  hostProperties: Config,
): ComposeConfigFn<Config, {}> {
  return createComposeFnFromState<Config, {}>({
    host: hostProperties,
    omit: [],
    aliases: {},
  });
}

/* -------------------------------------------------------------------------- */
/* doodBlockConfig                                                            */
/* -------------------------------------------------------------------------- */

export function doodBlockConfig<
  const SharedFields extends SharedFieldsDefinition,
  const Name extends string,
  const ComponentType extends string,
  const Config extends RequiredPropValues<SharedFields> &
    RequiredDescriptorControlValues<SharedFields>,
  const Layout extends DashboardLayoutFactoryInput<Config>,
>(
  metadata: DoodBlockMetadata<SharedFields, Name> & { readonly componentType: ComponentType },
  configFactory: (
    shared: SharedFieldAccessors<SharedFields>,
  ) => Config,
  dashboardLayoutFactory: () => ValidateDashboardLayoutFactoryResult<
    SharedFields,
    Config,
    Layout
  >,
): DoodBlockDefinition<Config> & PrefixedDoodBlockDefinition<Config, ComponentType> {
  const shared = buildSharedAccessors<SharedFields>(metadata.sharedFields);
  const config = configFactory(shared);
  const dashboardLayout = resolveDashboardLayout(
    dashboardLayoutFactory() as DashboardLayoutFactoryInput<Config>,
    config,
  );
  const blockDescriptor = buildBlockDescriptor(
    metadata,
    config,
    dashboardLayout.omitFields,
  );
  const blocDefinition = buildBlocDefinition(metadata, config);
  const resolved = buildResolvedConfig(config);
  const override = buildOverrideConfig(config);
  // Compat empty factory — real fixtures live in preview-scenarios sidecars.
  const scenarios = EMPTY_SCENARIO_FACTORY;
  const resolverDefinition = {
    properties: config,
    config: resolved,
    configToOverride: override as Partial<ResolvedConfig<Config>>,
  };
  const composeConfig = createComposeConfigFn(
    config as Record<string, unknown>,
  ) as ComposeConfigFn<Config & Record<string, unknown>, {}>;
  const definition = {
    blockDescriptor,
    blocDefinition,
    properties: config,
    config: resolved,
    configToOverride: override,
    resolveConfig: createBlockConfigResolver(resolverDefinition),
    dashboardLayout,
    scenarios,
  };
  return {
    ...definition,
    ...buildPrefixedDefinition(metadata.componentType, definition, composeConfig),
  } as DoodBlockDefinition<Config> & PrefixedDoodBlockDefinition<Config, ComponentType>;
}

export const scenarioBuilder: ScenarioBuilder = {
  describe: (name, scenario) => ({ name, ...scenario }),
};

export {
  definitionSetupScenarios,
  EMPTY_SCENARIO_FACTORY,
} from './definition-setup-scenarios';
export type { DefinitionSetupScenariosFn } from './definition-setup-scenarios';

function buildPrefixedDefinition<Config>(
  componentType: string,
  definition: DoodBlockDefinition<Config>,
  composeConfig: ComposeConfigFn<Config & Record<string, unknown>, {}>,
): Record<string, unknown> {
  const prefix = componentTypeToPrefix(componentType);
  return {
    [`${prefix}Descriptor`]: definition.blockDescriptor,
    [`${prefix}BlocDefinition`]: definition.blocDefinition,
    [`${prefix}Config`]: definition.config,
    [componentTypeToComposeConfigExportName(componentType)]: composeConfig,
    [`${prefix}Properties`]: definition.properties,
    [`${prefix}DashboardLayout`]: definition.dashboardLayout,
    [`${prefix}Scenarios`]: definition.scenarios,
    [`${prefix}DefinitionSetupScenarios`]: definitionSetupScenarios(componentType),
    [`${prefix}ResolveConfig`]: definition.resolveConfig,
  };
}

export function componentTypeToComposeConfigExportName(componentType: string): string {
  let base = componentType.endsWith('Component')
    ? componentType.slice(0, -'Component'.length)
    : componentType;
  if (base.endsWith('Atom')) {
    base = base.slice(0, -'Atom'.length);
  } else if (base.endsWith('Block')) {
    base = base.slice(0, -'Block'.length);
  }
  const camelBase = base.charAt(0).toLowerCase() + base.slice(1);
  return `compose${camelBase.charAt(0).toUpperCase()}${camelBase.slice(1)}Config`;
}

export function componentTypeToPrefix(componentType: string): string {
  const base = componentType.endsWith('Component')
    ? componentType.slice(0, -'Component'.length)
    : componentType;
  return base.charAt(0).toLowerCase() + base.slice(1);
}

export function buildSharedAccessors<SharedFields extends SharedFieldsDefinition>(
  sharedFields: SharedFields,
): SharedFieldAccessors<SharedFields> {
  return {
    props: buildAccessorTree(sharedFields.props ?? []),
    descriptorControlFields: buildAccessorTree(
      sharedFields.descriptorControlFields ?? [],
    ),
  } as SharedFieldAccessors<SharedFields>;
}

function buildAccessorTree(
  fields: readonly SharedFieldDefinition[],
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const { name, type } of fields) {
    const segments = name.split('.');
    let cursor: Record<string, unknown> = root;
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]!;
      if (i === segments.length - 1) {
        cursor[segment] = type;
      } else {
        const next = cursor[segment];
        cursor[segment] =
          next && typeof next === 'object'
            ? next
            : ({} as Record<string, unknown>);
        cursor = cursor[segment] as Record<string, unknown>;
      }
    }
  }
  return root;
}

// re-export path helpers for composition consumers
export { toSnakeHandle, lastSegment };
export type { Split, DotPath };
